import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import type { Chapter } from "../../types";
import { PanelHeader } from "./PanelHeader";
import { ExtractToolbar } from "./ExtractToolbar";
import { EmptyState } from "../ui/empty-state";
import { Field } from "../ui/field";
import { SectionCard } from "../ui/section-card";
import { IconOpenIn, IconRemove } from "./icons";
import {
  motifAppearanceForChapter,
  motifsInChapter,
  symbolAppearanceForChapter,
  symbolsInChapter,
  themeAppearanceForChapter,
  themesInChapter,
  type BookMotif,
  type BookSymbol,
  type BookTheme,
  type ThemeKind,
  type ThemeType,
} from "../../../shared/themeRoster";

function ThemeCard({
  theme,
  chapterId,
  onUpdateTheme,
  onOpenInGuide,
  onRequestRemove,
}: {
  theme: BookTheme;
  chapterId: string;
  onUpdateTheme: (id: string, patch: Partial<BookTheme>) => void;
  onOpenInGuide: (id: string, kind: ThemeKind) => void;
  onRequestRemove: (kind: ThemeKind, item: BookTheme) => void;
}) {
  const appearance = themeAppearanceForChapter(theme, chapterId);

  function updateManifestation(manifestation: string): void {
    const other = theme.appearances.filter((a) => a.chapterId !== chapterId);
    onUpdateTheme(theme.id, {
      appearances: [
        ...other,
        {
          chapterId,
          ...(manifestation.trim() ? { manifestation: manifestation.trim() } : {}),
        },
      ],
    });
  }

  return (
    <SectionCard
      bodyClassName="flex flex-col gap-2.5"
      title={theme.name}
      actions={
        <>
          <span className="text-xs text-muted-foreground">Theme</span>
          <Select
            value={theme.type}
            onValueChange={(value) => onUpdateTheme(theme.id, { type: value as ThemeType })}
          >
            <SelectTrigger className="min-w-26" size="sm" aria-label={`${theme.name} type`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="major">Major</SelectItem>
              <SelectItem value="minor">Minor</SelectItem>
            </SelectContent>
          </Select>
          <Button
            size="icon-sm"
            variant="ghost"
            type="button"
            aria-label="Open in Themes"
            title="Open in Themes"
            onClick={() => onOpenInGuide(theme.id, "theme")}
          >
            <IconOpenIn />
          </Button>
          <Button
            size="icon-sm"
            variant="destructive"
            type="button"
            aria-label="Remove theme"
            title="Remove theme"
            onClick={() => onRequestRemove("theme", theme)}
          >
            <IconRemove />
          </Button>
        </>
      }
    >
      <Field label="Name">
        <Input
          aria-label={`${theme.name} name`}
          value={theme.name}
          onChange={(e) => onUpdateTheme(theme.id, { name: e.target.value })}
        />
      </Field>
      <Field label="Idea (book)">
        <Textarea
          aria-label={`${theme.name} description`}
          className="min-h-0! text-[0.8rem]!"
          placeholder="Lasting statement of the theme…"
          value={theme.description}
          rows={2}
          onChange={(e) => onUpdateTheme(theme.id, { description: e.target.value })}
        />
      </Field>
      <Field label="Manifestation this chapter">
        <Textarea
          aria-label={`${theme.name} manifestation`}
          placeholder="How it appears in this chapter…"
          value={appearance?.manifestation ?? ""}
          rows={2}
          onChange={(e) => updateManifestation(e.target.value)}
        />
      </Field>
    </SectionCard>
  );
}

function MotifCard({
  motif,
  chapterId,
  onUpdateMotif,
  onOpenInGuide,
  onRequestRemove,
}: {
  motif: BookMotif;
  chapterId: string;
  onUpdateMotif: (id: string, patch: Partial<BookMotif>) => void;
  onOpenInGuide: (id: string, kind: ThemeKind) => void;
  onRequestRemove: (kind: ThemeKind, item: BookMotif) => void;
}) {
  const appearance = motifAppearanceForChapter(motif, chapterId);

  function updateContext(context: string): void {
    const other = motif.appearances.filter((a) => a.chapterId !== chapterId);
    onUpdateMotif(motif.id, {
      appearances: [
        ...other,
        {
          chapterId,
          ...(context.trim() ? { context: context.trim() } : {}),
        },
      ],
    });
  }

  return (
    <SectionCard
      bodyClassName="flex flex-col gap-2.5"
      title={motif.name}
      actions={
        <>
          <span className="text-xs text-muted-foreground">Motif</span>
          <Button
            size="icon-sm"
            variant="ghost"
            type="button"
            aria-label="Open in Themes"
            title="Open in Themes"
            onClick={() => onOpenInGuide(motif.id, "motif")}
          >
            <IconOpenIn />
          </Button>
          <Button
            size="icon-sm"
            variant="destructive"
            type="button"
            aria-label="Remove motif"
            title="Remove motif"
            onClick={() => onRequestRemove("motif", motif)}
          >
            <IconRemove />
          </Button>
        </>
      }
    >
      <Field label="Name">
        <Input
          aria-label={`${motif.name} name`}
          value={motif.name}
          onChange={(e) => onUpdateMotif(motif.id, { name: e.target.value })}
        />
      </Field>
      <Field label="Description (book)">
        <Textarea
          aria-label={`${motif.name} description`}
          placeholder="What the motif is and why it recurs…"
          value={motif.description}
          rows={2}
          onChange={(e) => onUpdateMotif(motif.id, { description: e.target.value })}
        />
      </Field>
      <Field label="Context this chapter">
        <Textarea
          aria-label={`${motif.name} context`}
          placeholder="How it appears in this chapter…"
          value={appearance?.context ?? ""}
          rows={2}
          onChange={(e) => updateContext(e.target.value)}
        />
      </Field>
    </SectionCard>
  );
}

function SymbolCard({
  symbol,
  chapterId,
  onUpdateSymbol,
  onOpenInGuide,
  onRequestRemove,
}: {
  symbol: BookSymbol;
  chapterId: string;
  onUpdateSymbol: (id: string, patch: Partial<BookSymbol>) => void;
  onOpenInGuide: (id: string, kind: ThemeKind) => void;
  onRequestRemove: (kind: ThemeKind, item: BookSymbol) => void;
}) {
  const appearance = symbolAppearanceForChapter(symbol, chapterId);

  function updateContext(context: string): void {
    const other = symbol.appearances.filter((a) => a.chapterId !== chapterId);
    onUpdateSymbol(symbol.id, {
      appearances: [
        ...other,
        {
          chapterId,
          ...(context.trim() ? { context: context.trim() } : {}),
        },
      ],
    });
  }

  return (
    <SectionCard
      bodyClassName="flex flex-col gap-2.5"
      title={symbol.name}
      actions={
        <>
          <span className="text-xs text-muted-foreground">Symbol</span>
          <Button
            size="icon-sm"
            variant="ghost"
            type="button"
            aria-label="Open in Themes"
            title="Open in Themes"
            onClick={() => onOpenInGuide(symbol.id, "symbol")}
          >
            <IconOpenIn />
          </Button>
          <Button
            size="icon-sm"
            variant="destructive"
            type="button"
            aria-label="Remove symbol"
            title="Remove symbol"
            onClick={() => onRequestRemove("symbol", symbol)}
          >
            <IconRemove />
          </Button>
        </>
      }
    >
      <Field label="Name">
        <Input
          aria-label={`${symbol.name} name`}
          value={symbol.name}
          onChange={(e) => onUpdateSymbol(symbol.id, { name: e.target.value })}
        />
      </Field>
      <Field label="Meaning (book)">
        <Textarea
          aria-label={`${symbol.name} meaning`}
          placeholder="What it stands for…"
          value={symbol.meaning}
          rows={2}
          onChange={(e) => onUpdateSymbol(symbol.id, { meaning: e.target.value })}
        />
      </Field>
      <Field label="Context this chapter">
        <Textarea
          aria-label={`${symbol.name} context`}
          placeholder="How it appears in this chapter…"
          value={appearance?.context ?? ""}
          rows={2}
          onChange={(e) => updateContext(e.target.value)}
        />
      </Field>
    </SectionCard>
  );
}

export function ChapterThemesPanel({
  activeChapter,
  themes,
  motifs,
  symbols,
  busy,
  extractModel,
  onExtractModelChange,
  openAiModels,
  anthropicModels,
  onExtract,
  onUpdateTheme,
  onUpdateMotif,
  onUpdateSymbol,
  onAdd,
  onOpenInGuide,
  onRequestRemoveTheme,
  onRequestRemoveMotif,
  onRequestRemoveSymbol,
}: {
  activeChapter: Chapter;
  themes: BookTheme[];
  motifs: BookMotif[];
  symbols: BookSymbol[];
  busy: boolean;
  extractModel: string;
  onExtractModelChange: (model: string, provider: "openai" | "anthropic") => void;
  openAiModels: ReadonlyArray<{ value: string; label: string }>;
  anthropicModels: ReadonlyArray<{ value: string; label: string }>;
  onExtract: () => void;
  onUpdateTheme: (id: string, patch: Partial<BookTheme>) => void;
  onUpdateMotif: (id: string, patch: Partial<BookMotif>) => void;
  onUpdateSymbol: (id: string, patch: Partial<BookSymbol>) => void;
  onAdd: (kind: ThemeKind) => void;
  onOpenInGuide: (id: string, kind: ThemeKind) => void;
  onRequestRemoveTheme: (theme: BookTheme) => void;
  onRequestRemoveMotif: (motif: BookMotif) => void;
  onRequestRemoveSymbol: (symbol: BookSymbol) => void;
}) {
  const chapterThemes = themesInChapter(themes, activeChapter.id);
  const chapterMotifs = motifsInChapter(motifs, activeChapter.id);
  const chapterSymbols = symbolsInChapter(symbols, activeChapter.id);
  const total = chapterThemes.length + chapterMotifs.length + chapterSymbols.length;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <PanelHeader
        title="Themes & motifs in this chapter"
        description="Literary themes, recurring motifs, and symbols grounded in the manuscript."
        actions={
          <>
            <ExtractToolbar
              model={extractModel}
              onModelChange={onExtractModelChange}
              openAiModels={openAiModels}
              anthropicModels={anthropicModels}
              onExtract={onExtract}
              extractBusy={busy}
            />
            <Button
              size="sm"
              variant="secondary"
              type="button"
              onClick={() => onAdd("theme")}
              disabled={busy}
            >
              Add theme
            </Button>
            <Button
              size="sm"
              variant="secondary"
              type="button"
              onClick={() => onAdd("motif")}
              disabled={busy}
            >
              Add motif
            </Button>
            <Button
              size="sm"
              variant="secondary"
              type="button"
              onClick={() => onAdd("symbol")}
              disabled={busy}
            >
              Add symbol
            </Button>
          </>
        }
      />

      <div className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto px-4 pt-3.5 pb-5">
        {total === 0 ? (
          <EmptyState
            title="No themes, motifs, or symbols linked to this chapter yet."
            description="Run Extract to pull them from the manuscript, or add one manually."
          />
        ) : (
          <div className="flex flex-col gap-2.5">
            {chapterThemes.map((theme) => (
            <ThemeCard
              key={theme.id}
              theme={theme}
              chapterId={activeChapter.id}
              onUpdateTheme={onUpdateTheme}
              onOpenInGuide={onOpenInGuide}
              onRequestRemove={(kind, item) => {
                if (kind === "theme") onRequestRemoveTheme(item as BookTheme);
              }}
            />
          ))}
          {chapterMotifs.map((motif) => (
            <MotifCard
              key={motif.id}
              motif={motif}
              chapterId={activeChapter.id}
              onUpdateMotif={onUpdateMotif}
              onOpenInGuide={onOpenInGuide}
              onRequestRemove={(kind, item) => {
                if (kind === "motif") onRequestRemoveMotif(item as BookMotif);
              }}
            />
          ))}
          {chapterSymbols.map((symbol) => (
            <SymbolCard
              key={symbol.id}
              symbol={symbol}
              chapterId={activeChapter.id}
              onUpdateSymbol={onUpdateSymbol}
              onOpenInGuide={onOpenInGuide}
              onRequestRemove={(kind, item) => {
                if (kind === "symbol") onRequestRemoveSymbol(item as BookSymbol);
              }}
            />
          ))}
          </div>
        )}
      </div>
    </div>
  );
}
